import CardRadioGroup from "#components/CardRadioGroup/index.js";
import DropdownSelect from "#components/DropdownSelect/index.js";
import IndeterminateIcon from "#components/IndeterminateIcon/index.js";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  Icon,
  LinearProgress,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Checkbox from "@mui/material/Checkbox";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import { grey } from "@mui/material/colors";
import { useTheme } from "@mui/material/styles";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import confidentialSticker from "assets/images/confidential.png";
import greenEmoji from "assets/images/emoji-green.png";
import orangeEmoji from "assets/images/emoji-orange.png";
import redEmoji from "assets/images/emoji-red.png";
import yellowEmoji from "assets/images/emoji-yellow.png";
import ifpcSticker from "assets/images/ifpc-cover.jpg";
import LoadingButton from "components/LoadingButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Dialog from "components/NoScrollDialog";
import {
  DEV_ANSWER_TYPE_ID,
  INTERNAL,
  PENDING_STATUS_ID,
  STD_ANSWER_TYPE_ID,
  NA_ANSWER_TYPE_ID,
} from "const";
import { useLanguage } from "contexts/language.context";
import dayjs from "dayjs";
import moment from "moment";
import React, { useEffect, useMemo, useRef } from "react";
import auditsService from "services/audits-service";
import SendEmailDialog from "../../Audit/dialogs/SendEmailDialog";
import {
  flagColor,
  flagColorBasedOnString,
  perfs,
  transformToTime,
} from "../../utils";
import downloadPdf from "../GenerateReport";
import styles from "../assessmentReportStyles.module.css";
import Constructs from "./Constructs";

const emojiBasedOnScore = (value, { size = "60px" } = {}) => {
  if (value < 75)
    return <img src={redEmoji} style={{ width: size, height: size }} />;
  if (value < 80)
    return <img src={orangeEmoji} style={{ width: size, height: size }} />;
  if (value < 90)
    return <img src={yellowEmoji} style={{ width: size, height: size }} />;
  return <img src={greenEmoji} style={{ width: size, height: size }} />;
};

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const CustomizedMenus = ({
  audit,
  loading,
  setLoading,
  includeTime,
  contact,
  summaryGroups,
  includeType,
  includeAssignee,
  includeTasksPhotos,
  includeTasks,
  includeTypeOfVisit,
  includeActionsChecklist,
  includePhotosChecklist,
  photosPerRow,
  groupBy,
  group,
}) => {
  const { label } = useLanguage();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [dlProgress, setDlProgress] = React.useState(0);
  const [dlIndeterminate, setDlIndeterminate] = React.useState(false);
  const [emailAnchorEl, setEmailAnchorEl] = React.useState(null);
  const [emailContent, setEmailContent] = React.useState({
    subject: "",
    from: "info",
    to: "",
    text: "",
    toName: "Mr. ",
    reportInfo: "",
    fromName: "QMS Department",
    fromFunction: "Senior Quality Management System Coordinator",
  });
  const [clickedButton, setClickedButton] = React.useState(0);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setEmailAnchorEl(null);
  };

  function saveBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  }

  const generateLink = async (sendAsEmail) => {
    if (!audit?.id) {
      return;
    }

    setDlProgress(0);
    setDlIndeterminate(true);
    setLoading("link");
    handleClose();
    try {
      const sentSummaryGroups = Object.keys(summaryGroups)
        .filter((key) => summaryGroups[key])
        .map((key) => Number(key) || "" + key);

      const query = {
        includeTime: includeTime ? "include" : "",
        contact: contact,
        includeType: includeType,
        includeAssignee: includeAssignee ? "include" : "",
        includeTasksPhotos: includeTasksPhotos && photosPerRow !== "none" ? "include" : "",
        includePhotosChecklist: includePhotosChecklist ? "include" : "",
        includeActionsChecklist: includeActionsChecklist ? "include" : "",
        typeOfVisit: includeTypeOfVisit,
        photosPerRow,
        groupBy: groupBy.join(","),
        group: group,
      };

      let body = {
        summaryGroups: sentSummaryGroups,
        includeTasks:
          Object.values(includeTasks).length &&
          Object.values(includeTasks).find((v) => !!v)
            ? {
                ...includeTasks,
                completed: includeTasks.completed
                  ? { from: includeTasks.completed.from.$d }
                  : false,
              }
            : "",
      };

      if (sendAsEmail) {
        query.to = emailContent.to;
        body = { ...emailContent, ...body };
      }

      const [response, data] = await auditsService.generateAssessmentReportLink(
        audit.id,
        query,
        body,
      );

      if (response.ok) {
        setLoading("download");
        setDlProgress(0);
        setDlIndeterminate(true);

        const dateText = new Date(audit.firstAnsweredAt || audit.date)
          .toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })
          .replace(/\//g, "_");

        const filename = `${audit.branch.branchGroup.name}-${audit.branch.name}-ifpc360-${dateText}.pdf`;

        await downloadPDF(data.url, filename, ({ percent, indeterminate }) => {
          if (indeterminate) {
            setDlIndeterminate(true);
          } else {
            setDlIndeterminate(false);
            setDlProgress(percent || 0);
          }
        });

        setLoading(false);
        return data;
      }
    } catch (err) {
      console.log("Error occurred when generating pdf link: ", err);
    }

    setLoading(false);
  };

  async function downloadPDF(url, filename, onProgress) {
    const response = await fetch(url);
    if (!response.ok)
      throw new Error(`HTTP ${response.status} ${response.statusText}`);

    const totalHeader = response.headers.get("content-length");
    const total = totalHeader ? parseInt(totalHeader, 10) : 0;

    // Some servers don't expose Content-Length over CORS.
    // We'll show indeterminate until we can compute a percentage.
    if (!response.body || !response.body.getReader) {
      const blob = await response.blob();
      onProgress?.({ percent: 100, indeterminate: false });
      saveBlob(blob, filename);
      return;
    }

    const reader = response.body.getReader();
    const chunks = [];
    let loaded = 0;

    if (total === 0) onProgress?.({ indeterminate: true });

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      loaded += value.byteLength;

      if (total > 0) {
        const percent = Math.max(
          0,
          Math.min(100, Math.round((loaded / total) * 100)),
        );
        onProgress?.({ percent, indeterminate: false });
      } else {
        onProgress?.({ indeterminate: true });
      }
    }

    const blob = new Blob(chunks, { type: "application/pdf" });
    onProgress?.({ percent: 100, indeterminate: false });
    saveBlob(blob, filename);
  }

  const handleGenerate = async () => {
    handleClose();
    setLoading(true);
    await downloadPdf(`${audit.description}`);
    setLoading(false);
  };

  useEffect(() => {
    if (!audit) return;
    const branch = audit.branch || {};
    const branchGroup = branch.branchGroup || {};
    const account = branchGroup.account || {};
    const accountManager = branch.accountManager || branchGroup.accountManager;
    setEmailContent({
      to: branch.email || branchGroup.email,
      subject: `IFPC x ${branchGroup.name} | ${
        branch.name
      } | 360° Assessment Report | ${
        months[new Date(audit.date).getMonth()]
      } ${new Date(audit.date).getFullYear()}`,
      reportInfo: `${branchGroup.name}, ${
        branch.name
      }, for ${months[new Date(audit.date).getMonth()]} ${new Date(
        audit.date,
      ).getFullYear()}`,
      fromName: accountManager?.name || account.name || branch.name || "",
      website: "",
      address: branch.address || "",
      logoUrl: account.logo || branch.logo || branchGroup.logo || "",
      accountNumber: account.phone || branch.phone || branchGroup.phone || "",
      contacts: [],
    });
  }, [audit]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flexWrap: "wrap",
        gap: 1,
        alignItems: "start",
      }}
    >
      {/* <LoadingButton
        disabled={loading}
        loading={loading && clickedButton == 0}
        variant="gradient"
        color="info"
        sx={{
          p: 1,
          px: 2,
          m: 0,
          display: "flex",
          flexDirection: "row",
          gap: 1,
        }}
        onClick={() => {
          setClickedButton(0);
          handleGenerate();
        }}
      >
        <Icon>file_download</Icon>
        {label("download-pdf-button")}
      </LoadingButton> */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 3,
          alignItems: "center",
          width: "100%",
        }}
      >
        <LoadingButton
          disabled={loading}
          loading={loading && clickedButton == 1}
          variant="gradient"
          color="info"
          sx={{
            p: 1,
            px: 2,
            m: 0,
            display: "flex",
            flexDirection: "row",
          }}
          onClick={() => {
            setClickedButton(1);
            generateLink(false);
          }}
        >
          <Icon sx={{ mr: 1 }}>file_download</Icon>
          {label("download-pdf-button")}

          {/* <MDBadge badgeContent="BETA" color="primary" /> */}
        </LoadingButton>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              height: "100%",
              width: "100%",
            }}
          >
            <Typography sx={{ fontSize: "1rem", color: "black" }}>
              {loading === "link"
                ? "Generating..."
                : dlIndeterminate
                  ? "Downloading..."
                  : `Downloading... ${dlProgress}%`}
            </Typography>
            {loading == "link" || dlIndeterminate ? (
              <LinearProgress color="info" />
            ) : (
              <LinearProgress
                color="info"
                variant={"determinate"}
                value={dlProgress}
              />
            )}
          </Box>
        ) : null}
      </Box>

      <LoadingButton
        disabled={loading}
        loading={loading && clickedButton == 2}
        variant="gradient"
        color="info"
        onClick={(e) => {
          setClickedButton(2);
          setEmailAnchorEl(Math.random());
        }}
        sx={{
          p: 1,
          px: 2,
          m: 0,
          display: "flex",
          flexDirection: "row",
        }}
      >
        <Icon sx={{ mr: 1 }}>send</Icon>
        {label("download-and-send-email")}
        {/* <MDBadge badgeContent="BETA" color="primary" /> */}
      </LoadingButton>
      {Boolean(emailAnchorEl) && (
        <SendEmailDialog
          open={Boolean(emailAnchorEl)}
          handleClose={handleClose}
          emailContent={emailContent || {}}
          setEmailContent={setEmailContent}
          handleSubmit={() => {
            generateLink(true);
          }}
        />
      )}
    </Box>
  );
};

export default function ReportDialog({
  isOpen,
  audit: initialAudit,
  summaryGroups,
  contacts,
  handleEditAuditInfo,
}) {
  initialAudit.pillars = initialAudit.pillars.map((pillar) => {
    return {
      ...pillar,
      kpis: pillar.kpis.map((kpi) => {
        return {
          ...kpi,
          weight: kpi?.auditKpis?.[0]?.weight ?? kpi.weight,
          auditKpis: [kpi.auditKpis[0]],
        };
      }),
    };
  });

  const { label, language } = useLanguage();
  const theme = useTheme();

  const [contactId, setContactId] = React.useState("");
  const [group, setGroup] = React.useState(1);
  const [photosPerRow, setPhotosPerRow] = React.useState(1);
  const [timeRecord, setTimeRecord] = React.useState("");
  const [includeType, setIncludeType] = React.useState(false);
  const [includeAssignee, setIncludeAssignee] = React.useState(false);
  const [includeTasksPhotos, setIncludeTasksPhotos] = React.useState(false);
  const [includeTasks, setIncludeTasks] = React.useState({
    open: false,
    inProgress: false,
    completed: false,
  });
  const [includeTypeOfVisit, setIncludeTypeOfVisit] = React.useState("");
  const [groupBy, setGroupBy] = React.useState([1]);
  const [firstAnswerTime, setFirstAnswerTime] = React.useState("");
  const [reportAttachments, setReportAttachments] = React.useState([]);
  const [includeActionsChecklist, setIncludeActionsChecklist] =
    React.useState(false);
  const [includePhotosChecklist, setIncludePhotosChecklist] =
    React.useState(false);

  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const audit = React.useMemo(() => {
    const newAudit = { ...initialAudit };
    if (group == 2) {
      let temp = [...newAudit.pillars];
      newAudit.pillars = newAudit.competencies;
      newAudit.competencies = temp;
    }

    return newAudit;
  }, [initialAudit, group]);

  async function getReportAttachments() {
    const [ok, data] = await auditsService.getReportAttachments(audit.id);

    if (ok) {
      setReportAttachments(data);
    }
  }

  React.useEffect(() => {
    if (!isOpen) return;
    setOpen(true);
    setLoading(false);
    setTimeRecord("");

    if (audit?.auditTypeId == 2) getReportAttachments();
  }, [isOpen, audit?.auditTypeId]);

  const handleClose = () => {
    setOpen(false);
  };

  const contactNames = useMemo(() => {
    if (!contactId || !contacts) return [];
    const contactsSelected = contacts?.filter((contactObj) =>
      contactId.find((v) => v == contactObj.id),
    );
    if (contactsSelected.length) {
      return contactsSelected.map((v) => v.name).join(", ");
    }
    return [];
  }, [contactId, contacts]);

  const pdfRef = useRef(null); // the item's index that is currently being dragged
  const draggedOverItem = useRef(0); // the item's index that we are currently dragging something else over it
  let scrollInterval;

  function getDragAfterElement(container, y) {
    const draggableElements = [
      ...container.querySelectorAll(".draggable-item:not(.dragging)"),
    ];
    return draggableElements.reduce(
      (closest, child) => {
        const box = child.getBoundingClientRect();
        const offset = y - box.top - box.height / 2;
        if (offset < 0 && offset > closest.offset) {
          return { offset: offset, element: child };
        } else {
          return closest;
        }
      },
      { offset: Number.NEGATIVE_INFINITY },
    ).element;
  }

  let { DashboardTable, reportScore } = useMemo(() => {
    const { score, dashboardTableHtml } = Constructs.DashboardTableConstruct(
      audit?.pillars,
      audit,
      group == 1 ? 1 : 2,
    );
    return { DashboardTable: dashboardTableHtml, reportScore: score };
  }, [audit]);

  const { CompetencyDashboardTable, competencyReportScore } = useMemo(() => {
    const { score, dashboardTableHtml } = Constructs.DashboardTableConstruct(
      audit?.competencies,
      audit,
      group == 1 ? 2 : 1,
    );
    return {
      CompetencyDashboardTable: dashboardTableHtml,
      competencyReportScore: score,
    };
  }, [audit]);

  if (groupBy.includes(2) && groupBy.length == 1)
    reportScore = competencyReportScore;

  const ActionPlanTable = useMemo(() => {
    return Constructs.ActionPlanTableConstruct(
      audit?.pillars,
      audit?.deviationOfBranch,
      audit.deviationCounts,
    );
  }, [audit]);

  const ActionPlanArray = useMemo(() => {
    return Constructs.ActionPlanTableConstructArray(
      audit?.pillars,
      audit?.deviationOfBranch,
      audit.deviationCounts,
    );
  }, [audit]);

  const ReportedPhotos = useMemo(() => {
    return Constructs.ReportedPhotosContruct(
      audit?.pillars,
      audit.auditTypeId == 2 ? null : DEV_ANSWER_TYPE_ID,
      label,
    );
  }, [audit, label]);

  const ReportedPhotosArray = useMemo(() => {
    return Constructs.ReportedPhotosContructArray(
      audit?.pillars,
      audit.auditTypeId == 2 ? null : DEV_ANSWER_TYPE_ID,
      label,
    );
  }, [audit, label]);

  const StandardPhotos = useMemo(() => {
    return Constructs.ReportedPhotosContruct(
      audit?.pillars,
      STD_ANSWER_TYPE_ID,
      label,
    );
  }, [audit, label]);

  const NaPhotos = useMemo(() => {
    return Constructs.ReportedPhotosContruct(audit?.pillars, NA_ANSWER_TYPE_ID, label);
  }, [audit, label]);

  const hasNaPhotos = useMemo(() => {
    return Constructs.countReportedPhotos(audit?.pillars, NA_ANSWER_TYPE_ID) > 0;
  }, [audit]);

  const StandardPhotosArray = useMemo(() => {
    return Constructs.ReportedPhotosContructArray(
      audit?.pillars,
      STD_ANSWER_TYPE_ID,
      label,
    );
  }, [audit, label]);

  const CheckListTable = useMemo(() => {
    return Constructs.CheckListTableConstruct(audit?.pillars, audit, {
      actions: includeActionsChecklist ? ActionPlanArray : null,
      photos: includePhotosChecklist && photosPerRow !== "none"
        ? [...(ReportedPhotosArray || []), ...(StandardPhotosArray || [])]
        : null,
    });
  }, [
    audit,
    includeActionsChecklist,
    includePhotosChecklist,
    photosPerRow,
    ActionPlanArray,
    ReportedPhotosArray,
    StandardPhotosArray,
  ]);

  const hasReportedPhotos = useMemo(() => {
    return (
      Constructs.countReportedPhotos(
        audit?.pillars,
        audit.auditTypeId == 2 ? null : DEV_ANSWER_TYPE_ID,
      ) > 0
    );
  }, [audit]);

  const hasStandardPhotos = useMemo(() => {
    return (
      Constructs.countReportedPhotos(audit?.pillars, STD_ANSWER_TYPE_ID) > 0
    );
  }, [audit]);

  const Generalities = useMemo(() => {
    if (!audit?.pillars) return;
    return Constructs.GeneralitiesConstruct(audit);
  }, [audit]);

  const PersonalPerceptionRating = useMemo(() => {
    if (!audit?.pillars) return;
    return Constructs.PersonalPerceptionRatingConstruct(audit.pillars);
  }, [audit]);

  const MysteryVisitNarrative = useMemo(() => {
    if (!audit?.pillars) return;
    return Constructs.MysteryVisitNarrativeConstruct(audit);
  }, [audit]);

  const ShopperCommentsAndObservations = useMemo(() => {
    if (!audit?.pillars) return;
    return Constructs.ShopperCommentsAndObservationsConstruct(audit.pillars);
  }, [audit]);

  const TasksPhotos = useMemo(() => {
    if (
      Object.values(includeTasks).length &&
      Object.values(includeTasks).find((v) => !!v) &&
      includeTasksPhotos && photosPerRow !== "none"
    ) {
      const tasks = audit.tasks.filter((task) => {
        if (
          includeTasks.completed &&
          task.statusId == 3 &&
          new Date(task.dueDate) > includeTasks.completed.from?.$d
        ) {
          return true;
        }

        if (includeTasks.inProgress && task.statusId == 2) {
          return true;
        }

        if (includeTasks.open && task.statusId == 1) {
          return true;
        }

        return false;
      });

      tasks.sort((a, b) => {
        return new Date(a.dueDate) - new Date(b.dueDate);
      });

      return Constructs.ReportedTasksPhotosContruct(tasks, label);
    } else return "";
  }, [audit, includeTasks, includeTasksPhotos, photosPerRow, label]);

  const hasTasksPhotos = useMemo(() => {
    if (
      Object.values(includeTasks).length &&
      Object.values(includeTasks).find((v) => !!v) &&
      includeTasksPhotos && photosPerRow !== "none"
    ) {
      const tasks = audit.tasks.filter((task) => {
        if (
          includeTasks.completed &&
          task.statusId == 3 &&
          new Date(task.dueDate) > includeTasks.completed.from?.$d
        ) {
          return true;
        }

        if (includeTasks.inProgress && task.statusId == 2) {
          return true;
        }

        if (includeTasks.open && task.statusId == 1) {
          return true;
        }

        return false;
      });

      return tasks.some((task) => {
        const attachments = task.inputAttachments?.length
          ? task.inputAttachments
          : task.auditKpi.attachments;

        return attachments?.length;
      });
    }

    return false;
  }, [audit, includeTasks, includeTasksPhotos, photosPerRow]);

  const Tasks = useMemo(() => {
    if (
      Object.values(includeTasks).length &&
      Object.values(includeTasks).find((v) => !!v)
    ) {
      const tasks = audit.tasks.filter((task) => {
        if (
          includeTasks.completed &&
          task.statusId == 3 &&
          new Date(task.dueDate) > includeTasks.completed.from?.$d
        ) {
          return true;
        }

        if (includeTasks.inProgress && task.statusId == 2) {
          return true;
        }

        if (includeTasks.open && task.statusId == 1) {
          return true;
        }

        return false;
      });

      tasks.sort((a, b) => {
        return new Date(a.dueDate) - new Date(b.dueDate);
      });

      return Constructs.TasksConstruct(tasks);
    } else return "";
  }, [audit, includeTasks]);

  useEffect(() => {
    if (audit?.branch?.colorPalette) {
      const {
        color1,
        color2,
        color3,
        fontColor1,
        fontColor2,
        fontColor3,
        ...rest
      } = audit?.branch?.colorPalette;
      document.documentElement.style.setProperty("--var-color-1", color1);
      document.documentElement.style.setProperty("--var-color-2", color2);
      document.documentElement.style.setProperty("--var-color-3", color3);

      document.documentElement.style.setProperty("--var-font-1", fontColor1);
      document.documentElement.style.setProperty("--var-font-2", fontColor2);
      document.documentElement.style.setProperty("--var-font-3", fontColor3);
    }
    // Add more variable changes as needed
  }, [audit]);

  let DISABLE_INCLUDE_TIME = true;
  const index = audit?.auditLogStatuses?.findIndex(
    (item) => item.statusId !== PENDING_STATUS_ID,
  );
  if (index !== -1) {
    DISABLE_INCLUDE_TIME = audit.firstAnsweredAt ? false : true;
  }
  const handleTimeCheckbox = (event) => {
    const checked = event.target.checked;
    if (checked) {
      const index = audit?.auditLogStatuses?.findIndex(
        (item) => item.statusId !== PENDING_STATUS_ID,
      );

      if (index !== -1) {
        const time = audit.auditLogStatuses[index].time;
        const timeString = transformToTime(time);
        setTimeRecord(timeString);
        let i = 0;

        setFirstAnswerTime(audit.firstAnsweredAt || "--/--/----");

        /*let minTime = null;
        for (i = 0; i < audit?.auditKpis?.length; i++) {
          const currentAuditKpiAnswer = audit.auditKpis[i].kpiValueTypeAnswer;

          if (currentAuditKpiAnswer) {
            if (minTime === null) {
              minTime = currentAuditKpiAnswer.createdAt;
            } else {
              minTime = findSmallestDate(
                minTime,
                currentAuditKpiAnswer.createdAt
              );
            }
          }
        }
        if (minTime) {
          setFirstAnswerTime(minTime);
        }*/
      } else {
        setTimeRecord("");
      }
    } else {
      setTimeRecord("");
    }
  };

  return (
    <Dialog fullWidth={true} maxWidth={"xl"} open={open}>
      <DialogTitle sx={{ borderBottom: "1px solid " + grey[300] }}>
        <MDBox display="flex" flexDirection="column">
          <MDBox
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
          >
            <MDTypography variant="h4">
              {label("assessment-report")}
            </MDTypography>
            <IconButton
              onClick={handleClose}
              disabled={loading}
              sx={{ padding: "0px" }}
            >
              <CloseIcon />
            </IconButton>
          </MDBox>
        </MDBox>
      </DialogTitle>
      <DialogContent
        sx={{
          padding: "0 !important",
          backgroundColor: "whitesmoke !important",
          display: "flex",
          justifyContent: "end",
          position: "relative",
        }}
      >
        <Box
          sx={{
            flex: 1,
            backgroundColor: "white !important",
            borderRight: "1px solid " + grey[300],
            position: "sticky",
            left: 0,
            top: 0,
            zIndex: 100,
            height: "calc(100vh - 120px)",
            overflowY: "auto",
          }}
        >
          <MDTypography variant="h6" sx={{ px: 2, py: 1.5, pb: 1 }}>
            {label("generate-based-on")}
          </MDTypography>
          <Grid container spacing={0}>
            <Grid
              item
              xs={12}
              sx={{
                px: 2,
              }}
            >
              <CardRadioGroup
                items={[
                  {
                    id: 1,
                    label: label("pillar-plural"),
                  },
                  {
                    id: 2,
                    label: label("competency-plural"),
                  },
                ]}
                value={group}
                onChange={(newValue) => {
                  setGroup(newValue?.id);
                }}
              />
            </Grid>
          </Grid>
          <MDTypography variant="h6" sx={{ px: 2, py: 1.5, pb: 1 }}>
            {label("setting-plural")}
          </MDTypography>
          <Grid container spacing={0}>
            <Grid item xs={12} sx={{ px: 2, py: 1 }}>
              <TextField
                select
                fullWidth
                label={label("kpi-pictures-per-row") || "KPI pictures per row"}
                value={photosPerRow}
                onChange={(event) => setPhotosPerRow(event.target.value === "none" ? "none" : Number(event.target.value))}
              >
                <MenuItem value="none">{label("none") || "None"}</MenuItem>
                {[1, 2, 3, 4].map((count) => (
                  <MenuItem key={count} value={count}>{count === 1 ? "1 (default)" : count}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                px: 2,
              }}
            >
              <FormLabel
                sx={{
                  mb: 1,
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                {label("group-dashboard-table-by")}
              </FormLabel>

              <FormGroup row sx={{ mt: -0.5 }}>
                {[
                  { id: group == 1 ? 1 : 2, label: label("pillar-plural") },
                  { id: group == 1 ? 2 : 1, label: label("competency-plural") },
                ].map((item) => {
                  const isChecked = groupBy.includes(item.id);

                  return (
                    <FormControlLabel
                      key={item.id}
                      label={item.label}
                      disabled={groupBy.length <= 1 && isChecked}
                      sx={{
                        "&.Mui-disabled": {
                          opacity: 0.8,
                        },
                      }}
                      control={
                        <Checkbox
                          checked={isChecked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setGroupBy([...groupBy, item.id]);
                            } else {
                              if (groupBy.length > 1) {
                                setGroupBy(
                                  groupBy.filter((id) => id !== item.id),
                                );
                              }
                            }
                          }}
                        />
                      }
                    />
                  );
                })}
              </FormGroup>
            </Grid>

            <Grid item xs={12} sx={{ px: 1 }}>
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Checkbox
                    checked={includeActionsChecklist}
                    onChange={(e) => {
                      setIncludeActionsChecklist(e.target.checked);
                    }}
                  />
                }
                label={label("show-actions-after-pillar")}
              />
            </Grid>
            <Grid item xs={12} sx={{ px: 1 }}>
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Checkbox
                    checked={includePhotosChecklist}
                    onChange={(e) => {
                      setIncludePhotosChecklist(e.target.checked);
                    }}
                  />
                }
                label={label("show-photos-after-pillar")}
              />
            </Grid>

            <Grid item xs={12} sx={{ px: 1 }}>
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Checkbox
                    checked={includeAssignee}
                    onChange={(e) => {
                      setIncludeAssignee(e.target.checked);
                    }}
                  />
                }
                label={label("show-assignee")}
              />
            </Grid>
            <Grid item xs={12} sx={{ px: 1 }}>
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Checkbox
                    checked={includeType}
                    onChange={(e) => {
                      setIncludeType(e.target.checked);
                    }}
                  />
                }
                label={label("show-audit-type")}
              />
            </Grid>
            <Grid item xs={12} sx={{ px: 1 }}>
              <Tooltip
                title={
                  DISABLE_INCLUDE_TIME
                    ? "You need to save the audit time first"
                    : ""
                }
                disableHoverListener={!DISABLE_INCLUDE_TIME}
                disableFocusListener={!DISABLE_INCLUDE_TIME}
                disableTouchListener={!DISABLE_INCLUDE_TIME}
              >
                <Box>
                  <FormControlLabel
                    sx={{ m: 0 }}
                    disabled={DISABLE_INCLUDE_TIME}
                    control={
                      <Checkbox
                        checked={timeRecord !== ""}
                        onChange={handleTimeCheckbox}
                      />
                    }
                    label={label("show-time")}
                  />
                </Box>
              </Tooltip>
            </Grid>

            <Grid item xs={12} sx={{ px: 1 }}>
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Checkbox
                    checked={Object.values(includeTasks).every((v) => !!v)}
                    indeterminate={
                      !Object.values(includeTasks).every((v) => !!v) &&
                      Object.values(includeTasks).find((v) => !!v)
                    }
                    indeterminateIcon={<IndeterminateIcon />}
                    onChange={(e) => {
                      if (e.target.checked) {
                        const targetDate = new Date();
                        targetDate.setMonth(targetDate.getMonth() - 3);
                        targetDate.setMinutes(0);
                        targetDate.setHours(0);
                        targetDate.setSeconds(0);
                        setIncludeTasks({
                          open: true,
                          inProgress: true,
                          completed: { from: dayjs(targetDate) },
                        });
                      } else {
                        setIncludeTasks({
                          open: false,
                          completed: false,
                          inProgress: false,
                        });
                      }
                    }}
                  />
                }
                label={label("show-tasks")}
              />

              <Box
                sx={{
                  pl: 2,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <FormControlLabel
                  sx={{ m: 0, mt: -1 }}
                  control={
                    <Checkbox
                      checked={includeTasks.open}
                      onChange={(e) => {
                        setIncludeTasks((t) => ({
                          ...t,
                          open: e.target.checked,
                        }));
                      }}
                    />
                  }
                  label={label("open-tasks")}
                />

                <FormControlLabel
                  disabled={!includeTasks}
                  sx={{ m: 0, mt: -1, opacity: includeTasks ? 1 : 0.6 }}
                  control={
                    <Checkbox
                      checked={includeTasks.inProgress}
                      onChange={(e) => {
                        setIncludeTasks((t) => ({
                          ...t,
                          inProgress: e.target.checked,
                        }));
                      }}
                    />
                  }
                  label={label("in-progress-tasks")}
                />

                <FormControlLabel
                  disabled={!includeTasks}
                  sx={{ m: 0, mt: -1, opacity: includeTasks ? 1 : 0.6 }}
                  control={
                    <Checkbox
                      checked={includeTasks.completed}
                      onChange={(e) => {
                        const targetDate = new Date();
                        targetDate.setMonth(targetDate.getMonth() - 3);
                        targetDate.setMinutes(0);
                        targetDate.setHours(0);
                        targetDate.setSeconds(0);

                        setIncludeTasks((t) => ({
                          ...t,
                          completed: e.target.checked
                            ? { from: dayjs(targetDate) }
                            : false,
                        }));
                      }}
                    />
                  }
                  label={
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <span>{label("completed-tasks-since")}</span>
                        <DatePicker
                          disabled={!includeTasks.completed}
                          format="DD/MM/YYYY"
                          value={
                            console.log(
                              "includeTasks.completed.from: ",
                              includeTasks.completed.from,
                            ) || includeTasks.completed
                              ? includeTasks.completed.from
                              : ""
                          }
                          onChange={(newDate) => {
                            setIncludeTasks((includeTasks) => ({
                              ...includeTasks,
                              completed: {
                                ...includeTasks.completed,
                                from: newDate,
                              },
                            }));
                          }}
                          slotProps={{
                            openPickerButton: {
                              size: "small",
                              sx: {
                                margin: 0,
                                padding: 0,
                              },
                            },
                            inputAdornment: {
                              position: "start",
                            },
                            textField: {
                              variant: "standard",
                              InputProps: {
                                disableUnderline: true,
                              },
                              sx: {
                                width: "106px",
                                minWidth: "0px !important",
                                border: "none",
                                "& input": {
                                  padding: 0,
                                  margin: 0,
                                  cursor: "pointer",
                                  fontWeight: "500 !important",
                                },
                              },
                            },
                          }}
                        />
                      </Box>
                    </LocalizationProvider>
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={12} sx={{ px: 1 }}>
              <Tooltip
                title={
                  !Object.values(includeTasks).find((v) => !!v)
                    ? "Show tasks is required"
                    : ""
                }
              >
                <FormControlLabel
                  sx={{
                    m: 0,
                    opacity: Object.values(includeTasks).find((v) => !!v)
                      ? 1
                      : 0.6,
                  }}
                  control={
                    <Checkbox
                      disabled={!Object.values(includeTasks).find((v) => !!v)}
                      checked={
                        includeTasksPhotos &&
                        Object.values(includeTasks).find((v) => !!v)
                      }
                      onChange={(e) => {
                        setIncludeTasksPhotos(e.target.checked);
                      }}
                    />
                  }
                  label={label("show-tasks-photos")}
                />
              </Tooltip>
            </Grid>
            {audit.auditTypeId == 2 ? (
              <Grid
                item
                xs={12}
                sx={{
                  px: 2,
                  pb: 2,
                  mb: 1,
                  py: 1,
                }}
              >
                <TextField
                  label={"Type of visit"}
                  value={includeTypeOfVisit}
                  onChange={(e) => {
                    setIncludeTypeOfVisit(e.target.value);
                  }}
                  fullWidth
                />
              </Grid>
            ) : null}
            <Grid
              item
              xs={12}
              sx={{
                px: 2,
                pb: 2,
                mb: 1,
              }}
            >
              <DropdownSelect
                items={
                  contacts
                    ? contacts?.map((user) => {
                        return { id: user.id, label: user.name };
                      })
                    : []
                }
                label={label("on-duty-contact")}
                initialValue={contactId}
                onChange={(newValue) => {
                  setContactId(newValue.map((v) => v.id));
                }}
                fullWidth
                multiple
              />
            </Grid>
            <Grid
              item
              xs={12}
              sx={{ borderBottom: "1px solid " + grey[300] }}
            />
            <Grid item xs={12}>
              <MDTypography variant="h6" sx={{ px: 2, py: 1.5, pb: 1 }}>
                {label("download-and-share")}
              </MDTypography>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                p: 2,
                pt: 1,
              }}
            >
              <CustomizedMenus
                summaryGroups={summaryGroups}
                audit={audit}
                loading={loading}
                setLoading={setLoading}
                contact={contactNames}
                includeType={includeType}
                includeAssignee={includeAssignee}
                includeTasksPhotos={includeTasksPhotos}
                includeTime={timeRecord != ""}
                includeTasks={includeTasks}
                includeTypeOfVisit={includeTypeOfVisit}
                groupBy={groupBy}
                group={group}
                includeActionsChecklist={includeActionsChecklist}
                includePhotosChecklist={includePhotosChecklist}
                photosPerRow={photosPerRow}
              />
            </Grid>
          </Grid>
        </Box>
        <div
          className={`${styles["pdf-container"]} ${photosPerRow > 1 ? styles["multi-photo-layout"] : ""}`}
          style={{ "--photos-per-row": photosPerRow, "--photo-image-size": `${220 - photosPerRow * 20}px` }}
          ref={pdfRef}
          onDragOver={(e) => {
            e.preventDefault();
            const afterElement = getDragAfterElement(pdfRef.current, e.clientY);
            const draggable = document.querySelector(".dragging");
            if (afterElement == null) {
              pdfRef.current.appendChild(draggable);
            } else {
              pdfRef.current.insertBefore(draggable, afterElement);
            }
          }}
        >
          {/* cover */}
          <div
            className={`${styles["pdf-item-container"]} printable`}
            id="cover"
          >
            <div className={styles["pdf-item"]}>
              <div className={styles["report-cover"]}>
                <Avatar
                  className={styles["report-cover-logo"]}
                  variant="rounded"
                  src={audit?.branch?.logo} // `${UPLOAD_URL}/${audit?.branch?.logo}`
                ></Avatar>
                <div className={styles["cover-info-container-box"]}>
                  <div className={styles["cover-info-container"]}>
                    <div className={styles["assessment"]}>
                      <span className={styles["deg"]}>360&deg;</span>
                      <span className={styles["text"]}>
                        {audit.auditTypeId == 2
                          ? "Mystery Shopping"
                          : label("assessment-report")}
                      </span>
                    </div>
                    <div>
                      <span
                        style={
                          contactId === "" && timeRecord === ""
                            ? { fontSize: "1.5rem" }
                            : {}
                        }
                      >
                        {audit?.branch?.name}
                      </span>
                      {contactId !== "" ? (
                        <span
                          style={{ display: "block" }}
                        >{`${label("on-duty-contact")}: ${contactNames}`}</span>
                      ) : null}
                      {timeRecord !== "" ? (
                        <span
                          style={{ display: "block" }}
                        >{`${label("audit-time")}: ${moment(
                          new Date(firstAnswerTime)
                            .toISOString()
                            .replace("z", ""),
                        ).format("DD/MM/YYYY hh:mm A")}`}</span>
                      ) : null}
                      {includeTypeOfVisit ? (
                        <span>Type of visit: {includeTypeOfVisit}</span>
                      ) : null}
                      {includeAssignee ? (
                        <span>{`${label("audited-by")}: ${audit?.assignedTo?.name || ""}`}</span>
                      ) : null}
                      {audit?.branch?.branchGroup?.reportExtras?.trim() ? (
                        <span>
                          {audit.branch.branchGroup.reportExtras?.trim()}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  {audit.auditTypeId != 1 && (
                    <div className={styles["cover-info-rating"]}>
                      <span>
                        <span>{label("rating-singular")}</span>
                        {audit.auditTypeId == 2 || isNaN(reportScore) ? null : (
                          <img
                            className={`${styles.flag} ${styles.bx} ${styles["bxs-flag"]}}`}
                            src={flagColor(reportScore)}
                            alt="flag-score"
                          />
                        )}

                        {audit.auditTypeId == 2
                          ? emojiBasedOnScore(reportScore, {
                              sx: {
                                fontSize: "64px !important",
                                color: "black !important",
                                marginLeft: "10px",
                              },
                            })
                          : null}
                      </span>
                      <span>
                        {isNaN(reportScore) ? "-" : reportScore + "%"}
                      </span>
                    </div>
                  )}
                </div>
                {includeType ? (
                  <div
                    style={{
                      width: "100%",
                      padding: "0 12px",
                    }}
                  >
                    <span
                      style={{
                        backgroundColor: "var(--var-color-3)",
                        color: "var(--var-font-3)",
                        padding: "6px",
                        borderRadius: "4px",
                        fontWeight: "bold",
                      }}
                    >
                      {audit?.branch?.branchGroup?.accountId == 3
                        ? label("external-assessment")
                        : audit.type == INTERNAL
                          ? label("internal-assessment")
                          : label("external-assessment")}
                    </span>
                  </div>
                ) : null}

                <div className={styles["cover-confidental"]}>
                  <img src={ifpcSticker} alt="ifpc" />
                  <img src={confidentialSticker} alt="confidential" />
                </div>
                <div className={styles["cover-footer"]}>
                  <span>{label("assessment-report-rights")}</span>
                </div>
              </div>
            </div>
          </div>

          {/* dashboard table */}
          {audit.auditTypeId != 1 && (
            <div
              className={styles["draggable-item"]}
              draggable
              onDragStart={(event) => {
                event.target.classList.add("dragging");
                scrollInterval = setInterval(() => {
                  const container = pdfRef.current;
                  if (
                    container.scrollTop + container.offsetHeight >=
                    container.scrollHeight
                  ) {
                    clearInterval(scrollInterval);
                  } else {
                    container.scrollTop += 1;
                  }
                }, 50); // Adjust the scroll speed as needed
              }}
              onDragEnd={(event) => {
                event.target.classList.remove("dragging");
                clearInterval(scrollInterval);
              }}
            >
              <div
                className={`${styles["pdf-item-container"]} title printable`}
              >
                <div className={styles["pdf-margin"]}></div>
                <div className={styles["pdf-item"]}>
                  <div className={styles["title"]}>
                    {audit.auditTypeId == 2
                      ? "Mystery Shopping Experience Dashboard"
                      : label("store-evaluation-dashboard")}
                  </div>
                </div>
                <div className={styles["pdf-margin"]}></div>
              </div>
              {groupBy.includes(1) ? DashboardTable : null}
              <div
                style={{
                  height: "10px",
                  backgroundColor: "white",
                }}
              />
              {groupBy.includes(2) ? CompetencyDashboardTable : null}

              {audit.auditTypeId == 2 ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    backgroundColor: "white",
                  }}
                >
                  <div className={styles["pdf-item"]}>
                    <div className={styles["generalities-ratings-container"]}>
                      <div className={styles["generalities-ratings"]}>
                        <div className={styles["generalities-rating"]}>
                          {emojiBasedOnScore(100)}
                          <span>90% - 100% Excellent</span>
                        </div>
                        <div className={styles["generalities-rating"]}>
                          {emojiBasedOnScore(85)}

                          <span>80% - 89% Good - Improvement Required</span>
                        </div>
                        <div className={styles["generalities-rating"]}>
                          {emojiBasedOnScore(76)}
                          <span>
                            75% - 79% Standards - Required Improvement Actions
                          </span>
                        </div>
                        <div className={styles["generalities-rating"]}>
                          {emojiBasedOnScore(10)}

                          <span>0% - 74% Fail - High Level of Activity</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          )}

          {/* Generalities */}
          {audit.auditTypeId == 2 ? (
            <div className={styles["draggable-item"]}>
              <div
                className={`${styles["pdf-item-container"]} ${styles["title"]} ${styles["printable"]}`}
              >
                <div className={styles["pdf-margin"]}></div>
                <div className={styles["pdf-item"]}>
                  <div className={styles["title"]}>Generalities</div>
                </div>
                <div className={styles["pdf-margin"]}></div>
              </div>
              {Generalities}
            </div>
          ) : null}

          {/* risk analysis - static table */}
          {/* performance ratings - static table */}
          {audit.auditTypeId != 1 && audit.auditTypeId != 2 && (
            <div
              className={`${styles["pdf-item-container"]} ${styles["draggable-item"]}`}
              draggable
              onDragStart={(event) => {
                event.target.classList.add("dragging");
                scrollInterval = setInterval(() => {
                  const container = pdfRef.current;
                  if (
                    container.scrollTop + container.offsetHeight >=
                    container.scrollHeight
                  ) {
                    clearInterval(scrollInterval);
                  } else {
                    container.scrollTop += 1;
                  }
                }, 50); // Adjust the scroll speed as needed
              }}
              onDragEnd={(event) => {
                event.target.classList.remove("dragging");
                clearInterval(scrollInterval);
              }}
            >
              <div
                className={`${styles["pdf-item-container"]} printable no-title`}
              >
                <div className={styles["pdf-margin"]}></div>
                <div className={styles["pdf-item"]}>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <div className={styles["pdf-item"]}>
                      <div className={styles["subtitle"]}>
                        <div className={styles["line"]}></div>
                        {label("risk-analysis")}
                        <div className={styles["line"]}></div>
                      </div>
                      <div className={styles["risk-analysis-table"]}>
                        <div>
                          <Constructs.Table
                            theads={[
                              label("risk-range"),
                              label("weight-singular"),
                            ]}
                            rows={[
                              [label("low-singular"), "1"],
                              [label("moderate-singular"), "2"],
                              [label("critical-singular"), label("above-3")],
                            ]}
                          />
                        </div>
                      </div>
                    </div>
                    <div className={styles["pdf-item"]}>
                      <div className={styles["subtitle"]}>
                        <div className={styles["line"]}></div>
                        {label("performance-ratings")}
                        <div className={styles["line"]}></div>
                      </div>
                      <div className={styles["perf-ratings"]}>
                        <div className={styles["perfs"]}>
                          {perfs(label).map((perf, index) => {
                            return (
                              <div
                                className={styles["perf"]}
                                key={"Perf: " + index}
                              >
                                <span>{perf.title}</span>
                                <div className={styles["arrow"]}>
                                  <div className={styles["arrow-tail"]}></div>
                                  <div className={styles["arrow-head"]}></div>
                                </div>
                                <span>{perf.value}%</span>
                                <img
                                  className={`${styles.flag} ${styles.bx} ${styles["bxs-flag"]}}`}
                                  src={flagColorBasedOnString(perf.color)}
                                  alt="flag-score"
                                />
                              </div>
                            );
                          })}
                        </div>
                        <div className={styles["perf-levels"]}>
                          <span>{label("low-singular")}</span>
                          <div className={`${styles.arrow} ${styles.vertical}`}>
                            <div className={styles["arrow-tail"]}></div>
                            <div className={styles["arrow-head"]}></div>
                          </div>
                          <span>{label("high-singular")}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={styles["pdf-margin"]}></div>
              </div>
            </div>
          )}

          {/* abbreviations list */}
          {audit.auditTypeId != 2 ? (
            <div className={`${styles["draggable-item"]}`}>
              <div
                className={`${styles["pdf-item-container"]} title printable`}
                id="abbreviation"
              >
                <div className={styles["pdf-margin"]}></div>
                <div className={styles["pdf-item"]}>
                  <div className={styles["title"]}>
                    {label("list-of-abbreviations")}
                  </div>
                </div>
                <div className={styles["pdf-margin"]}></div>
              </div>
              <div
                className={`${styles["pdf-item-container"]} printable`}
                id="abbreviation"
              >
                <div className={styles["pdf-margin"]}></div>
                <div className={styles["pdf-item"]}>
                  <div className={styles["abbreviation-list"]}>
                    <ol>
                      <li>{label("std-standard")}</li>
                      <li>{label("dev-deviation")}</li>
                      <li>{label("na-not-applicable")}:</li>
                    </ol>
                    <ol>
                      <li>{label("na-a")}</li>
                      <li>{label("na-b")}</li>
                      <li>{label("na-c")}</li>
                    </ol>
                  </div>
                </div>
                <div className={styles["pdf-margin"]}></div>
              </div>
            </div>
          ) : null}

          {/* Personal Perception Rating */}
          {audit.auditTypeId == 2 ? (
            <div className={styles["draggable-item"]}>
              <div
                className={`${styles["pdf-item-container"]} ${styles["title"]} ${styles["printable"]}`}
                id="checklist"
              >
                <div className={styles["pdf-margin"]}></div>
                <div className={styles["pdf-item"]}>
                  <div className={styles["title"]}>
                    Personal Perception Rating (PPR)
                  </div>
                </div>
                <div className={styles["pdf-margin"]}></div>
              </div>
              {PersonalPerceptionRating}
            </div>
          ) : null}

          {/* Personal Perception Rating */}
          {audit.auditTypeId == 2 ? (
            <div className={styles["draggable-item"]}>
              <div
                className={`${styles["pdf-item-container"]} ${styles["title"]} ${styles["printable"]}`}
                id="checklist"
              >
                <div className={styles["pdf-margin"]}></div>
                <div className={styles["pdf-item"]}>
                  <div className={styles["title"]}>
                    Shopper Comments & Observations
                  </div>
                </div>
                <div className={styles["pdf-margin"]}></div>
              </div>
              {ShopperCommentsAndObservations}
            </div>
          ) : null}

          {audit.auditTypeId == 2 && audit.narrativeText ? (
            <div className={styles["draggable-item"]}>
              <div
                className={`${styles["pdf-item-container"]} ${styles["title"]} ${styles["printable"]}`}
                id="checklist"
              >
                <div className={styles["pdf-margin"]}></div>
                <div className={styles["pdf-item"]}>
                  <div className={styles["title"]}>Mystery Visit Narrative</div>
                </div>
                <div className={styles["pdf-margin"]}></div>
              </div>
              {MysteryVisitNarrative}
            </div>
          ) : null}

          {/* checklist table */}
          <div className={styles["draggable-item"]}>
            <div
              className={`${styles["pdf-item-container"]} title printable`}
              id="checklist"
            >
              <div className={styles["pdf-margin"]}></div>
              <div className={styles["pdf-item"]}>
                <div className={styles["title"]}>{label("checklist")}</div>
              </div>
              <div className={styles["pdf-margin"]}></div>
            </div>
            {CheckListTable}
          </div>
          {/* action plans */}
          {audit.auditTypeId != 1 &&
            audit.auditTypeId != 2 &&
            !includeActionsChecklist && (
              <div
                className={styles["draggable-item"]}
                draggable
                onDragStart={(event) => {
                  event.target.classList.add("dragging");
                  scrollInterval = setInterval(() => {
                    const container = pdfRef.current;
                    if (
                      container.scrollTop + container.offsetHeight >=
                      container.scrollHeight
                    ) {
                      clearInterval(scrollInterval);
                    } else {
                      container.scrollTop += 1;
                    }
                  }, 50); // Adjust the scroll speed as needed
                }}
                onDragEnd={(event) => {
                  event.target.classList.remove("dragging");
                  clearInterval(scrollInterval);
                }}
              >
                <div
                  className={`${styles["pdf-item-container"]} printable title`}
                  id="action"
                >
                  <div className={styles["pdf-margin"]}></div>
                  <div className={styles["pdf-item"]}>
                    <div className={`${styles["title"]}`}>
                      {label("action-plan")}
                    </div>
                  </div>
                  <div className={styles["pdf-margin"]}></div>
                </div>
                {ActionPlanTable}
              </div>
            )}
          {/* Deviation Reported Photos */}
          {photosPerRow !== "none" && hasReportedPhotos && !includePhotosChecklist ? (
            <div
              className={styles["draggable-item"]}
              draggable
              onDragStart={(event) => {
                event.target.classList.add("dragging");
                scrollInterval = setInterval(() => {
                  const container = pdfRef.current;
                  if (
                    container.scrollTop + container.offsetHeight >=
                    container.scrollHeight
                  ) {
                    clearInterval(scrollInterval);
                  } else {
                    container.scrollTop += 1;
                  }
                }, 50); // Adjust the scroll speed as needed
              }}
              onDragEnd={(event) => {
                event.target.classList.remove("dragging");
                clearInterval(scrollInterval);
              }}
            >
              <div
                className={`${styles["pdf-item-container"]} printable title`}
                id="photos"
              >
                <div className={styles["pdf-margin"]}></div>
                <div className={styles["pdf-item"]}>
                  <div className={styles["title"]}>
                    {audit.auditTypeId == 2
                      ? label("reported-photos")
                      : label("deviation-photos")}
                  </div>
                </div>
                <div className={styles["pdf-margin"]}></div>
              </div>
              {ReportedPhotos}
            </div>
          ) : null}

          {/* Standard Photos */}
          {audit.auditTypeId != 2 &&
          photosPerRow !== "none" && hasStandardPhotos &&
          !includePhotosChecklist ? (
            <div
              className={styles["draggable-item"]}
              draggable
              onDragStart={(event) => {
                event.target.classList.add("dragging");
                scrollInterval = setInterval(() => {
                  const container = pdfRef.current;
                  if (
                    container.scrollTop + container.offsetHeight >=
                    container.scrollHeight
                  ) {
                    clearInterval(scrollInterval);
                  } else {
                    container.scrollTop += 1;
                  }
                }, 50); // Adjust the scroll speed as needed
              }}
              onDragEnd={(event) => {
                event.target.classList.remove("dragging");
                clearInterval(scrollInterval);
              }}
            >
              <div
                className={`${styles["pdf-item-container"]} printable title`}
                id="photos"
              >
                <div className={styles["pdf-margin"]}></div>
                <div className={styles["pdf-item"]}>
                  <div className={styles["title"]}>
                    {label("standard-photos")}
                  </div>
                </div>
                <div className={styles["pdf-margin"]}></div>
              </div>
              {StandardPhotos}
            </div>
          ) : null}

          {/* Tasks */}
          {includeTasks &&
          Object.values(includeTasks).length &&
          Object.values(includeTasks).find((v) => !!v) ? (
            <div className={styles["draggable-item"]}>
              <div
                className={`${styles["pdf-item-container"]} printable title`}
              >
                <div className={styles["pdf-margin"]}></div>
                <div className={styles["pdf-item"]}>
                  <div className={styles["title"]}>{label("task-plural")}</div>
                </div>
                <div className={styles["pdf-margin"]}></div>
              </div>
              {Tasks}
            </div>
          ) : null}

          {/* Tasks Photos */}
          {photosPerRow !== "none" && includeTasksPhotos && hasTasksPhotos ? (
            <div
              className={styles["draggable-item"]}
              draggable
              onDragStart={(event) => {
                event.target.classList.add("dragging");
                scrollInterval = setInterval(() => {
                  const container = pdfRef.current;
                  if (
                    container.scrollTop + container.offsetHeight >=
                    container.scrollHeight
                  ) {
                    clearInterval(scrollInterval);
                  } else {
                    container.scrollTop += 1;
                  }
                }, 50); // Adjust the scroll speed as needed
              }}
              onDragEnd={(event) => {
                event.target.classList.remove("dragging");
                clearInterval(scrollInterval);
              }}
            >
              <div
                className={`${styles["pdf-item-container"]} printable title`}
                id="photos"
              >
                <div className={styles["pdf-margin"]}></div>
                <div className={styles["pdf-item"]}>
                  <div className={styles["title"]}>{label("tasks-photos")}</div>
                </div>
                <div className={styles["pdf-margin"]}></div>
              </div>
              {TasksPhotos}
            </div>
          ) : null}

          {/* N/A Photos */}
          {audit.auditTypeId != 2 &&
          photosPerRow !== "none" && hasNaPhotos ? (
            <div
              className={styles["draggable-item"]}
              draggable
              onDragStart={(event) => {
                event.target.classList.add("dragging");
                scrollInterval = setInterval(() => {
                  const container = pdfRef.current;
                  if (
                    container.scrollTop + container.offsetHeight >=
                    container.scrollHeight
                  ) {
                    clearInterval(scrollInterval);
                  } else {
                    container.scrollTop += 1;
                  }
                }, 50); // Adjust the scroll speed as needed
              }}
              onDragEnd={(event) => {
                event.target.classList.remove("dragging");
                clearInterval(scrollInterval);
              }}
            >
              <div
                className={`${styles["pdf-item-container"]} printable title`}
                id="na-photos"
              >
                <div className={styles["pdf-margin"]}></div>
                <div className={styles["pdf-item"]}>
                  <div className={styles["title"]}>
                    {label("na-photos") || (language.id === "ar" ? "صور غير منطبقة" : "N/A photos")}
                  </div>
                </div>
                <div className={styles["pdf-margin"]}></div>
              </div>
              {NaPhotos}
            </div>
          ) : null}

          {/* tables container */}
          <div
            className={styles["draggable-item"]}
            draggable
            onDragStart={(event) => {
              event.target.classList.add("dragging");
              scrollInterval = setInterval(() => {
                const container = pdfRef.current;
                if (
                  container.scrollTop + container.offsetHeight >=
                  container.scrollHeight
                ) {
                  clearInterval(scrollInterval);
                } else {
                  container.scrollTop += 1;
                }
              }, 50); // Adjust the scroll speed as needed
            }}
            onDragEnd={(event) => {
              event.target.classList.remove("dragging");
              clearInterval(scrollInterval);
            }}
            id="table-hidden-container"
          ></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
